import { Box, Grid, Typography } from "@mui/material";
import Stack from "@mui/material/Stack";
import Warning from "assets/noDataWarning.png";
import ThemeContext from "components/app/Context";
import Feedback from "components/common/Feedback";
import { filterPage } from "components/common/Filters/FiltersHelper";
import GlobalFilterDescription from "components/common/Filters/GlobalFilterDescription";
import GlobalFilters from "components/common/Filters/GlobalFilters";
import NoPlansBanner from "components/common/NoPlansBanner";
import ScrollAwareHeader from "components/common/ScrollAwareHeader";
import Scroller from "components/common/Scroller/InfiniteScroller";
import TemplateItem from "components/common/plans/item";
import { fetchAbovePropertyMyPlansAsync, setLoader } from "components/features/AboveProperty/AboveProperty.slice";
import FilterButtons from "components/features/AboveProperty/FilterButtons";
import { uniqBy } from "lodash";
import { useContext, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { convertArrayValuesToString, formFilterRequestPayload, getUserHltId } from "utils/CommonFunctions";
import { pageNames } from "../../../../utils/Types";
import CreateMyPlan from "../Create";
import TableHeader from "../TableHeader";
import MyPlansItem from "../item";
import { fetchOwnerDetailsAsync, setMyPlanSlice } from "../myplans.slice";

export const PROPERTY_PLAN = "PLAN";
export const TEMPLATE_PLAN = "TEMPLATE_PLAN";

interface Props {
  type?: string;
}
function List(props: Props) {
  const { type } = props;
  const isPropertyPlan = type === PROPERTY_PLAN;
  const filterPageName = filterPage.inncodePlansDisplay;

  const { plansList, totalList, createNewPlan }: any = useAppSelector((state) => state?.aboveProperty.planProgress);
  const searchTextAppSlice = useAppSelector((state) => state.app.searchText);

  const { functionalAreas } = useAppSelector((state) => state?.aboveProperty.filters.myPlans["templatePlans"]); // template plan filters

  // Property plan filters
  const globalFiltersforPage = useAppSelector((state) => state.globalFilters[filterPageName]);
  const filterRequestPayload = globalFiltersforPage?.modifiedFilter || globalFiltersforPage?.defaultFilter || [];
  const formattedFilterRequestPayload = formFilterRequestPayload(filterRequestPayload, convertArrayValuesToString);
  const { isConsultant, isCorp }: any = useAppSelector((state) => state.app.loggedInUserDetails);
  const filtersSearchText = globalFiltersforPage.searchText;

  const searchText = isPropertyPlan ? filtersSearchText : searchTextAppSlice;

  const [pageNo, setPageNo] = useState(0);
  const dispatch = useAppDispatch();
  const { isMobileScreen } = useContext(ThemeContext);

  const payloadData = {
    includeOwners: true,
    planType: type,
    searchText,
    searchedHltId: getUserHltId(),
    functionalArea: functionalAreas.join(","),
    ...formattedFilterRequestPayload
  };

  useEffect(() => {
    const getPlans = async () => {
      let planOwners: [] = [];
      const payload = {
        includeFilters: true,
        ...payloadData
      };
      const page = 0;
      const props = { payload, page };
      const fetchPlans = await dispatch(fetchAbovePropertyMyPlansAsync(props));
      const uniqueBrands = uniqBy(
        fetchPlans.payload?.meta?.filters
          .map((item: any) => {
            return { key: item.brandCode, value: item.brandName };
          })
          .filter((element: any) => !!element?.value)
          .sort((a: any, b: any) => a?.value.localeCompare(b?.value)),
        "key"
      );
      dispatch(setMyPlanSlice(uniqueBrands));
      planOwners = fetchPlans?.payload?.meta?.owners;
      if (planOwners?.length > 0) {
        const data = {
          value: planOwners,
          size: planOwners.length
        };
        await dispatch(fetchOwnerDetailsAsync(data));
      }
      dispatch(setLoader(false));
    };
    getPlans();
    setPageNo(0);
  }, [dispatch, filterRequestPayload, createNewPlan, functionalAreas, searchText]);

  const fetchMore = () => {
    const page = pageNo + 1;
    const props = { payload: payloadData, page };

    dispatch(fetchAbovePropertyMyPlansAsync(props));
    setPageNo(pageNo + 1);
  };

  const getScrollHeaderContent = () => {
    return (
      <ScrollAwareHeader sx={{}}>
        <Grid display="flex" alignItems="center" width="100%" gap="8px">
          <Box flex="1" minWidth={0}>
            {isPropertyPlan ? (
              <GlobalFilters
                isSearchBarVisible
                page={filterPageName}
                isPreDefinedNav
                searchedText={filtersSearchText}
              />
            ) : (
              <Box
                sx={{
                  mt: isMobileScreen ? "-12px" : "-15px",
                  mb: isMobileScreen ? "-30px" : "-10px"
                }}
              >
                <FilterButtons
                  isPreDefinedNav
                  isSearchBarVisible
                  searchPredefinedValue={searchTextAppSlice}
                  isFunctionalAreaVisible
                  filterType="templatePlans"
                  parentView="myPlans"
                  minWidth="350px"
                />
              </Box>
            )}
          </Box>
          {!isCorp && !isConsultant && !isMobileScreen && <CreateMyPlan />}
        </Grid>
      </ScrollAwareHeader>
    );
  };

  return (
    <>
      <Feedback form="fetchMyPlans" />
      {getScrollHeaderContent()}
      <div>
        {isPropertyPlan ? (
          <Box paddingY={"20px"}>
            <GlobalFilters
              isSearchBarVisible
              page={filterPageName}
              isPreDefinedNav={true}
              searchedText={filtersSearchText}
            />
            <GlobalFilterDescription bgColor={tokens.Background.General.Primary} page={filterPageName} />
          </Box>
        ) : (
          <FilterButtons
            isPreDefinedNav
            isSearchBarVisible
            searchPredefinedValue={searchTextAppSlice}
            isFunctionalAreaVisible
            filterType={"templatePlans"}
            parentView="myPlans"
          />
        )}
      </div>
      {!isMobileScreen && isPropertyPlan && <TableHeader />}
      <Grid container marginTop={isMobileScreen ? "-16px" : "0px"} marginBottom="24px">
        {plansList?.length ? (
          <Scroller dataLength={plansList.length} loadMore={fetchMore} totalList={totalList} label="Plans">
            {plansList.map((plan: any) => {
              return isPropertyPlan ? (
                <MyPlansItem key={plan.id} plan={plan} />
              ) : (
                <TemplateItem key={plan.id} item={plan} />
              );
            })}
          </Scroller>
        ) : (
          <>
            {isPropertyPlan ? (
              <NoPlansBanner pageName={pageNames.myPlans} />
            ) : (
              <Stack
                spacing={3}
                alignItems="center"
                justifyContent="center"
                sx={{
                  backgroundColor: tokens.Background.General.Secondary,
                  minHeight: "500px",
                  width: "100%",
                  padding: "8px, 16px, 8px, 16px",
                  borderRadius: "8px",
                  gap: "24px",
                  border: `1px solid ${tokens.Border.General.Secondary}`
                }}
              >
                <img src={Warning} alt="No Data Warning" className="no-data-banner-icon" />
                <Typography variant="body1" color={tokens.Text.General.Tertiary}>
                  You have no {isPropertyPlan ? "Property" : "Template"} Plans yet
                </Typography>
              </Stack>
            )}
          </>
        )}
      </Grid>
    </>
  );
}

export default List;
